import {
  Box,
  BoxProps,
  Button,
  Container,
  Flex,
  Heading,
  SimpleGrid,
  Skeleton,
  Text,
} from '@chakra-ui/react';
import CardLoader from '@core/components/Loader/Card/CardLoader';
import { CDN_IMAGES_URL, commonConfig } from '@core/config';
import { useQuery } from '@tanstack/react-query';
import { CMS } from '@waitroom/models';
import { getQueryRequestData, integrationsProvidersQuery } from '@waitroom/react-query';
import { repeat } from '@waitroom/react-utils';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { NotetakerGrid } from '../../../../integrations/components/NotetakerGrid';
import { useIsUnlistedCategory } from '../../../../integrations/hooks/useIntegrations';
import { useRecallIntegration } from '../../../../integrations/hooks/useRecallIntegration';
import { CustomIntegrationCard, IntegrationCard } from '../../Card/Integration/IntegrationCard';

const loader = (
  <Box textAlign="center">
    <Skeleton maxW="200px" h={12} mb={4} />
    <Skeleton maxW="350px" h={8} mb={12} />
    <SimpleGrid spacing={6} gridTemplateColumns="repeat(auto-fill, minMax(300px, 1fr))">
      {repeat(<CardLoader header={false} />, 3)}
    </SimpleGrid>
  </Box>
);
const gCalIcon = `${CDN_IMAGES_URL}/google-calendar.svg`;
const outlookCalIcon = `${CDN_IMAGES_URL}/outlook.png`;

const Integrations = ({ ...rest }: CMS.Integrations & BoxProps): ReactElement | null => {
  const { t } = useTranslation();
  const providersQuery = useQuery(integrationsProvidersQuery);
  const isUnlisted = useIsUnlistedCategory();
  const all = getQueryRequestData(providersQuery);
  const recallCalendar = useRecallIntegration();

  return (
    <Box {...rest}>
      <Container maxW="container.2xl">
        <Box mb={32}>
          <NotetakerGrid {...recallCalendar}>
            <Heading as="h2" textAlign="center" size="4xl" mb={8}>
              {t(`integrations.recallCalendar.title`)}
            </Heading>
            <Text mb={12} fontSize="xl" textAlign="center">
              {t('integrations.recallCalendar.description')}
            </Text>
          </NotetakerGrid>
        </Box>
        <Box mb={32}>
          <Heading as="h2" textAlign="center" size="4xl" mb={8}>
            {t('integrations.categories.scheduling')}
          </Heading>
          <Text mb={12} fontSize="xl" textAlign="center">
            {t('integrations.categories.schedulingDesc')}
          </Text>
          <SimpleGrid spacing={6} gridTemplateColumns="repeat(auto-fill, minMax(300px, 1fr))">
            <CustomIntegrationCard
              provider={{
                name: 'google-calendar',
                label: t('integrations.gCal.label'),
                description: t('integrations.gCal.desc'),
                icon: { url: gCalIcon },
                integrations: [],
              }}
              category="integrations.categories.scheduling"
            >
              <Button
                as="a"
                href={commonConfig.links.googleCalendar}
                target="_blank"
                rel="noreferrer noopener"
                size="sm"
                fontSize="md"
                variant="outline"
                colorScheme="gray.900"
                w="full"
                mt="auto"
              >
                {t('global.install')}
              </Button>
            </CustomIntegrationCard>
            {/* Disabled temporary until extension is ready */}
            {/* <CustomIntegrationCard
            provider={{
              name: 'chrome-extension',
              label: t('integrations.gChrome.label'),
              description: t('integrations.gChrome.desc'),
              icon: { url: chromeIcon },
              integrations: [],
              }}
              category="integrations.categories.scheduling"
              >
              <Button
              as="a"
              href={config.links.chromeExtension}
              target="_blank"
              rel="noreferrer noopener"
              size="sm"
              fontSize="md"
              variant="outline"
              colorScheme="gray.900"
              w="full"
              mt="auto"
              >
              {t('global.install')}
              </Button>
              </CustomIntegrationCard> */}
            <CustomIntegrationCard
              provider={{
                name: 'outlook-calendar',
                label: t('integrations.outlookCal.label'),
                description: t('integrations.outlookCal.desc'),
                icon: { url: outlookCalIcon },
                integrations: [],
              }}
              category="integrations.categories.scheduling"
            >
              <Button
                as="a"
                href={commonConfig.links.outlookCalendar}
                target="_blank"
                rel="noreferrer noopener"
                size="sm"
                fontSize="md"
                variant="outline"
                colorScheme="gray.900"
                w="full"
                mt="auto"
              >
                {t('global.install')}
              </Button>
            </CustomIntegrationCard>
          </SimpleGrid>
        </Box>
        <Flex direction="column" gap={32}>
          {providersQuery.isLoading
            ? loader
            : all?.toReversed().map(({ providers, label, name }) => (
                <Box w="full" key={label}>
                  <Heading as="h2" size="4xl" textAlign="center" mb={8}>
                    {t(label)}
                  </Heading>
                  <Text mb={12} fontSize="xl" textAlign="center">
                    {t(`${label}Desc`)}
                  </Text>
                  <SimpleGrid
                    spacing={6}
                    gridTemplateColumns="repeat(auto-fill, minMax(300px, 1fr))"
                  >
                    {providers.map((provider) => (
                      <IntegrationCard
                        key={provider.name}
                        provider={provider}
                        category={label}
                        isUnlisted={isUnlisted(name)}
                      />
                    ))}
                  </SimpleGrid>
                </Box>
              ))}
        </Flex>
      </Container>
    </Box>
  );
};
export default Integrations;
